import { useCallback, useEffect, useState } from 'react'
import { signaturesApi, type SectorRuleView, type SignatureProviderInfo } from '../api/signatures'
import { errMsg } from '../lib/financeFormat'
import { useFeatureFlag } from '../lib/featureFlags'
import { Callout, Card, CardHeader, DataTable, DataTableEmptyRow, Select, StatusPill } from './ui'

/**
 * R5-02 (Z-12) — per ogni settore, quale sistema di firma si usa. Senza regola vale il predefinito: operatore per i
 * settori con operatori (telefonia, energia), firma Equal per gli altri. Lettura per il backoffice, modifica solo admin
 * (il server risponde 403 agli altri: l'errore si mostra nella card).
 */
export function SignatureSectorRulesCard() {
  const enabled = useFeatureFlag('firma.equal')
  const [providers, setProviders] = useState<SignatureProviderInfo[]>([])
  const [sectors, setSectors] = useState<SectorRuleView[] | null>(null)
  const [hidden, setHidden] = useState(false)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState<number | null>(null)

  const load = useCallback(() => {
    signaturesApi.sectorRules()
      .then(r => { setProviders(r.providers); setSectors(r.sectors) })
      .catch(e => { if (/403|404|forbidden|not found/i.test(errMsg(e, ''))) setHidden(true); else setError(errMsg(e, 'Regole di firma non disponibili.')) })
  }, [])

  useEffect(() => { if (enabled) load() }, [enabled, load])
  if (!enabled || hidden) return null

  async function change(departmentId: number, value: string) {
    setBusy(departmentId); setError('')
    try { await signaturesApi.setSectorRule(departmentId, value || null); load() } catch (e) { setError(errMsg(e, 'Regola non salvata.')) } finally { setBusy(null) }
  }

  return (
    <Card variant="outlined" padding="none" className="mt-3">
      <CardHeader
        icon="signature"
        title="Firma dei contratti per settore"
        subtitle="Senza regola: sistema dell'operatore per i settori con operatori, firma Equal per gli altri."
      />
      {error && <div className="px-3 pt-2"><Callout tone="error">{error}</Callout></div>}
      <DataTable variant="plain" aria-label="Firma per settore">
        <thead><tr><th>Settore</th><th>Sistema di firma</th><th>Origine</th></tr></thead>
        <tbody>
          {sectors && sectors.length === 0 && <DataTableEmptyRow colSpan={3} title="Nessun settore." />}
          {sectors?.map(s => (
            <tr key={s.departmentId}>
              <td>{s.departmentName}{!s.isActive && <span className="text-muted"> (non attivo)</span>}</td>
              <td style={{ minWidth: 240 }}>
                <Select
                  label="Sistema"
                  labelHidden
                  value={s.source === 'regola' ? s.provider : ''}
                  disabled={busy === s.departmentId}
                  onChange={e => void change(s.departmentId, e.currentTarget.value)}
                >
                  <option value="">Predefinito ({providers.find(p => p.code === s.provider && s.source !== 'regola')?.label ?? 'automatico'})</option>
                  {providers.map(p => <option key={p.code} value={p.code}>{p.label}</option>)}
                </Select>
              </td>
              <td><StatusPill tone={s.source === 'regola' ? 'info' : 'neutral'}>{s.source === 'regola' ? 'Regola' : 'Predefinito'}</StatusPill></td>
            </tr>
          ))}
        </tbody>
      </DataTable>
    </Card>
  )
}
